import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, useParams } from "react-router-dom";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";
import "../assets/css/userReferrals.css";

const numberFormat = new Intl.NumberFormat();
const dateFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const UserReferralDetail = () => {
  const { userId } = useParams();
  const location = useLocation();
  const periodParams = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      period: query.get("period") || "today",
      ...(query.get("startDate") ? { startDate: query.get("startDate") } : {}),
      ...(query.get("endDate") ? { endDate: query.get("endDate") } : {}),
    };
  }, [location.search]);
  const [users, setUsers] = useState([]);
  const [referrer, setReferrer] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios
      .get(`wallet/admin/referrals/${userId}`, {
        params: { start: page * rowsPerPage, limit: rowsPerPage, ...periodParams },
      })
      .then((response) => {
        if (!active) return;
        if (!response.data.status) throw new Error(response.data.message);
        setUsers(response.data.data || []);
        setReferrer(response.data.referrer || null);
        setTotal(response.data.pagination?.total || 0);
      })
      .catch((requestError) => {
        if (!active) return;
        setUsers([]);
        setTotal(0);
        setError(
          requestError.response?.data?.message ||
            requestError.message ||
            "Unable to load referred users.",
        );
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [page, periodParams, rowsPerPage, userId]);

  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const firstEntry = total ? page * rowsPerPage + 1 : 0;
  const lastEntry = Math.min((page + 1) * rowsPerPage, total);
  const backUrl = `/admin/userReferrals?${new URLSearchParams(periodParams).toString()}`;

  return (
    <section className="family-clans-page user-referrals-page" aria-labelledby="referred-users-title">
      <div className="page-title">
        <div className="row align-items-end">
          <div className="col-12 col-md-7">
            <h3 id="referred-users-title" className="mb-2 text-white">
              Users referred by {referrer?.name || "user"}
            </h3>
          </div>
          <div className="col-12 col-md-5">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-md-end">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={backUrl} className="text-danger">User Referrals</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Referred users</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card family-clans-card">
        <div className="card-header family-clans-toolbar">
          <div>
            <span className="family-clans-count">{numberFormat.format(total)}</span>
            <span className="family-clans-count-label"> referred users</span>
          </div>
          <Link to={backUrl} className="btn btn-sm btn-outline-light">
            <i className="fas fa-arrow-left me-2" aria-hidden="true"></i>Back to referrals
          </Link>
        </div>

        <div className="card-body card-overflow p-0">
          <div className="table-responsive">
            <table className="table family-clans-table mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>RCoin</th>
                  <th>Diamond</th>
                  <th>IP</th>
                  <th>Created at</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="5" className="family-clans-state">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading referred users…
                  </td></tr>
                )}
                {!loading && error && (
                  <tr><td colSpan="5" className="family-clans-state text-danger">{error}</td></tr>
                )}
                {!loading && !error && users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="family-clan-identity">
                        <img
                          src={user.image || noImage}
                          onError={(event) => { event.currentTarget.src = noImage; }}
                          alt=""
                        />
                        <strong>{user.name || "Unnamed user"}</strong>
                      </div>
                    </td>
                    <td>{numberFormat.format(user.rCoin || 0)}</td>
                    <td>{numberFormat.format(user.diamond || 0)}</td>
                    <td><code>{user.ip || "—"}</code></td>
                    <td>{user.createdAt ? dateFormat.format(new Date(user.createdAt)) : "—"}</td>
                  </tr>
                ))}
                {!loading && !error && !users.length && (
                  <tr><td colSpan="5" className="family-clans-state">No referred users found for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !error && total > 0 && (
          <div className="card-footer family-clans-pagination">
            <span>Showing {firstEntry}–{lastEntry} of {total}</span>
            <div className="family-clans-page-controls">
              <label>
                Rows{" "}
                <select className="form-select form-select-sm" value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
              <button className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page">&lsaquo;</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} aria-label="Next page">&rsaquo;</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UserReferralDetail;
